import { useEffect, useRef, useState } from "react";
import { useFaqs } from "@/hooks/useSiteContent";
import ContentStatus from "@/components/feature/ContentStatus";

export default function FaqSection() {
  const { data: faqs, loading, error, reload } = useFaqs();
  const [visible, setVisible] = useState(false);
  const [openId, setOpenId] = useState<number | null>(1);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 },
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="w-full bg-background-50 py-14 md:py-20">
      <div className="w-full px-4 md:px-8 lg:px-12">
        <div
          className={`text-center mb-10 md:mb-14 transition-all duration-700 ${
            visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
          }`}
        >
          <span className="inline-flex items-center gap-1.5 px-4 py-1.5 bg-primary-100 text-primary-700 text-xs md:text-sm font-label font-medium rounded-full mb-4">
            <i className="ri-question-line" />
            Câu hỏi thường gặp
          </span>
          <h2 className="font-display text-2xl md:text-4xl font-bold text-foreground-950 mb-3">
            Giải đáp thắc mắc của bạn
          </h2>
        </div>

        <ContentStatus loading={loading} error={error} onRetry={reload}>
        <div className="max-w-3xl mx-auto flex flex-col gap-3">
          {faqs.map((faq) => {
            const isOpen = openId === faq.id;
            return (
              <div
                key={faq.id}
                className={`bg-background-50 border rounded-lg overflow-hidden transition-colors ${
                  isOpen ? "border-primary-300" : "border-background-200/70"
                }`}
              >
                <button
                  onClick={() => setOpenId(isOpen ? null : faq.id)}
                  className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left cursor-pointer"
                  aria-expanded={isOpen}
                >
                  <span className="font-display text-sm md:text-base font-semibold text-foreground-950">
                    {faq.question}
                  </span>
                  <div
                    className={`w-8 h-8 flex items-center justify-center rounded-full flex-shrink-0 transition-all ${
                      isOpen
                        ? "bg-primary-500 text-background-50"
                        : "bg-background-100 text-foreground-600"
                    }`}
                  >
                    <i
                      className={`ri-${
                        isOpen ? "subtract" : "add"
                      }-line text-lg transition-transform`}
                    />
                  </div>
                </button>
                <div
                  className={`grid transition-all duration-300 ${
                    isOpen
                      ? "grid-rows-[1fr] opacity-100"
                      : "grid-rows-[0fr] opacity-0"
                  }`}
                >
                  <div className="overflow-hidden">
                    <p className="px-5 pb-5 text-sm text-foreground-600 leading-relaxed font-body">
                      {faq.answer}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        </ContentStatus>
      </div>
    </section>
  );
}